/**
 * Mission hero lotus animation.
 *
 * This is the inline animated version of the supplied
 * /media/brand/chi-lotus-mark.svg asset. Keeping the asset's path geometry
 * inline lets GSAP draw each petal and move the tracer along the actual mark,
 * rather than approximating it with a second logo.
 */
import { useEffect, useRef } from "react";
import gsap from "gsap";
import DrawSVGPlugin from "gsap/DrawSVGPlugin";
import MotionPathPlugin from "gsap/MotionPathPlugin";

gsap.registerPlugin(DrawSVGPlugin, MotionPathPlugin);

const BRAND = {
  magenta: "#DC3C91",
  gold: "#DBA514",
  green: "#4AB857",
  deepGreen: "#2C4E3F",
  outline: "#F7F8F7",
} as const;

const LOTUS_PETALS = [
  {
    id: "left-base",
    color: BRAND.green,
    order: 0,
    d: "M248 241C214 272 190 301 147 307C99 314 54 291 11 241C37 211 67 186 99 177C110 200 115 240 248 241Z",
  },
  {
    id: "right-base",
    color: BRAND.green,
    order: 1,
    d: "M248 241C282 272 306 301 349 307C397 314 442 291 485 241C459 211 429 186 397 177C386 200 381 240 248 241Z",
  },
  {
    id: "center",
    color: BRAND.deepGreen,
    order: 2,
    d: "M186 91C151 111 120 145 99 177C110 200 115 240 248 241C381 240 386 200 397 177C376 145 345 111 310 91C276 110 251 151 248 205C245 151 220 110 186 91Z",
  },
  {
    id: "left-middle",
    color: BRAND.magenta,
    order: 3,
    d: "M79 71C122 69 157 74 186 91C174 125 182 171 226 218C190 188 146 168 99 177C84 147 77 111 79 71Z",
  },
  {
    id: "right-middle",
    color: BRAND.magenta,
    order: 4,
    d: "M417 71C374 69 339 74 310 91C322 125 314 171 270 218C306 188 350 168 397 177C412 147 419 111 417 71Z",
  },
  {
    id: "top",
    color: BRAND.gold,
    order: 5,
    d: "M248 2C278 31 299 60 310 91C276 110 251 151 248 205C245 151 220 110 186 91C197 60 218 31 248 2Z",
  },
] as const;

const LOTUS_PATH = LOTUS_PETALS.map(petal => petal.d).join(" ");

export default function MissionLotusMark({ size = 220 }: { size?: number }) {
  const rootRef = useRef<SVGSVGElement>(null);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const q = gsap.utils.selector(root);
    const outlines = q<SVGPathElement>(".lotus-outline");
    const petals = q<SVGPathElement>(".lotus-petal");
    const motionPath = q<SVGPathElement>(".lotus-motion-path")[0];
    const tracer = q<SVGCircleElement>(".lotus-motion-tracer")[0];
    const sparkles = q<SVGGElement>(".lotus-sparkle-star");
    if (!motionPath || !tracer || !sparkles.length) return;

    const tl = gsap.timeline({
      repeat: -1,
      repeatDelay: 1.2,
      defaults: { ease: "power2.inOut" },
    });
    timelineRef.current = tl;

    tl
      .set(petals, { opacity: 0 })
      .set(outlines, { drawSVG: "0%" })
      .to(outlines, {
        drawSVG: "100%",
        duration: 1.35,
        stagger: (_index: number, target: SVGPathElement) =>
          Number(target.dataset.order ?? 0) * 0.18,
      })
      .to(petals, {
        opacity: 1,
        duration: 0.34,
        stagger: 0.16,
        ease: "power1.out",
      })
      .set(sparkles, { visibility: "visible" })
      .fromTo(
        sparkles,
        { scale: 0.12, transformOrigin: "center center" },
        { scale: 1, duration: 0.28, stagger: 0.08, ease: "back.out(2.2)" },
      )
      .to(sparkles, {
        scale: 0.12,
        duration: 0.24,
        stagger: 0.08,
        ease: "power2.in",
      })
      .set(sparkles, { visibility: "hidden" })
      .set(tracer, { visibility: "hidden", clearProps: "x,y,transform" })
      .set(tracer, { visibility: "visible" }, "-=0.15")
      .to(tracer, {
        motionPath: {
          path: motionPath,
          align: motionPath,
          alignOrigin: [0.5, 0.5],
          autoRotate: false,
        },
        duration: 4.2,
        ease: "sine.inOut",
      })
      .set(tracer, { visibility: "hidden", clearProps: "x,y,transform" });

    return () => {
      timelineRef.current?.kill();
      timelineRef.current = null;
    };
  }, []);

  return (
    <div className="mission-lotus" style={{ width: size }}>
       <svg
        ref={rootRef}
        viewBox="0 0 496 326"
        width="100%"
        height="100%"
        role="img"
        aria-label="The CHI Alliance lotus mark"
        className="mission-lotus__svg"
      >
        {/* Exact supplied guide path; it is hidden and only drives the tracer. */}
        <path
          className="lotus-motion-path"
          d={LOTUS_PATH}
          fill="none"
          stroke="none"
          visibility="hidden"
          aria-hidden="true"
        />

         {/* Keep each fill immediately below its own outline, matching the supplied SVG's
             stacking order so a later pink petal masks the center boundary beneath it. */}
        {LOTUS_PETALS.map(petal => (
           <g key={`petal-${petal.id}`}>
             <path
               className="lotus-petal"
               d={petal.d}
               fill={petal.color}
               stroke="none"
             />
             <path
               className="lotus-outline"
               data-order={petal.order}
                d={petal.d}
               fill="none"
               stroke={BRAND.outline}
               strokeWidth="3.5"
               strokeLinecap="round"
               strokeLinejoin="round"
             />
           </g>
        ))}

         {/* A brief set of glints celebrates the completed draw without changing
             the lotus's fill treatment. The timeline keeps these hidden until all
             outlines are visible, then hides them before the tracer begins. */}
         <g className="lotus-sparkle" aria-hidden="true">
           <g className="lotus-sparkle-star" visibility="hidden">
             <path d="M98 34V66M82 50H114" fill="none" stroke={BRAND.outline} strokeWidth="3.5" strokeLinecap="round" />
             <path d="M89 41L107 59M107 41L89 59" fill="none" stroke={BRAND.gold} strokeWidth="2" strokeLinecap="round" />
           </g>
           <g className="lotus-sparkle-star" visibility="hidden">
             <path d="M396 47V77M381 62H411" fill="none" stroke={BRAND.outline} strokeWidth="3.5" strokeLinecap="round" />
             <path d="M388 54L404 70M404 54L388 70" fill="none" stroke={BRAND.magenta} strokeWidth="2" strokeLinecap="round" />
           </g>
           <g className="lotus-sparkle-star" visibility="hidden">
             <path d="M248 273V299M235 286H261" fill="none" stroke={BRAND.outline} strokeWidth="3.5" strokeLinecap="round" />
             <path d="M241 279L255 293M255 279L241 293" fill="none" stroke={BRAND.outline} strokeWidth="2" strokeLinecap="round" />
           </g>
         </g>

        <circle
          className="lotus-motion-tracer"
           cx="248"
           cy="241"
          r="8"
           fill="#FFFFFF"
           visibility="hidden"
          aria-hidden="true"
        />
      </svg>

      <style>{`
        .mission-lotus{
          max-width:72vw;
          aspect-ratio:496/326;
          margin:0 auto .3rem;
          filter:drop-shadow(0 0 26px rgba(220,60,145,.28)) drop-shadow(0 0 56px rgba(74,184,87,.16));
        }
        .mission-lotus__svg{
          display:block;
          width:100%;
          height:auto;
          overflow:visible;
        }
         .lotus-petal{opacity:0}
         @media (prefers-reduced-motion: reduce){
           .lotus-petal{opacity:1}
         }
        .lotus-motion-path{display:none}
      `}</style>
    </div>
  );
}
